import { UIIcon } from '../../config/icons';
import { LScope } from '../mdl/l-scope';
import { meEmailLogin, meForgotPassword, MEIdentityInfo, meIdentityLogin, MELoginEnvironment, meResetPassword, meSignup } from '../me/login';
import { UIAside } from '../ui/aside';
import { UIButton } from '../ui/component/button';
import { UIDialog } from '../ui/component/dialog';
import { UITextInput } from '../ui/component/input';
import { UIAutofill } from '../ui/component/input-base';
import { uiToast, uiToastAndRethrow } from '../ui/component/toast';
import { $el } from '../ui/elements';
import { $recaptcha } from '../ui/media/recaptcha';
import { $analytics } from '../y/analytics';
import { $apple } from '../y/apple';
import { $form } from '../y/form';
import { $i18n } from '../y/i18n';
import { ABEmailCheck } from './widgets';

interface ABLoginManagerOptions {
    close?: boolean;
    mobile_fs?: boolean;
    no_forgot?: boolean;

    extra_params?: MELoginEnvironment;
    prefilled_email?: string;

    on_success: () => void;
    on_signup?: () => void;
    on_identity?: (info: MEIdentityInfo) => void;
}

export class ABLoginManager {
    scope: LScope;
    options: ABLoginManagerOptions;

    constructor(scope: LScope, options: ABLoginManagerOptions) {
        this.scope = scope;
        this.options = options;
    }

    showLoginDialog(replace?: UIAside, replace_back?: boolean) {
        new LoginDialog(this).present({ replace: replace, replace_back: replace_back });
    }

    showSignupDialog(replace?: UIAside, replace_back?: boolean) {
        new SignupDialog(this).present({ replace: replace, replace_back: replace_back });
    }

    showResetDialog(replace?: UIAside, replace_back?: boolean) {
        new ResetDialog(this).present({ replace: replace, replace_back: replace_back });
    }

    emailLogin(email: string, password: string) {
        return meEmailLogin(email, password, this.scope, this.options.extra_params || {}).then(this.options.on_success, uiToastAndRethrow);
    }

    emailSignup(data: any) {
        return meSignup(data, this.scope, this.options.extra_params || {}).then(this.options.on_success, uiToastAndRethrow);
    }

    appleLogin() {
        return $apple.login().then(token => {
            return meIdentityLogin('apple', token, this.scope, this.options.extra_params || {}).then(info => {
                if(info) {
                    this.options.on_identity!(info);
                } else {
                    this.options.on_success();
                }
            }, uiToastAndRethrow);
        });
    }
}

class LoginDialog extends UIDialog {
    constructor(manager: ABLoginManager) {
        super({ close: manager.options.close, classes: ['m-440', manager.options.mobile_fs && 'm-mobile-fs'] });

        $apple.init();

        const email = new UITextInput({ type: 'email', autofill: UIAutofill.USERNAME, placeholder: $i18n.tl.ph_email, required: true });
        const password = new UITextInput({ type: 'password', autofill: UIAutofill.PASSWORD, placeholder: $i18n.tl.ph_password, required: true });

        if(manager.options.prefilled_email)
            email.value = manager.options.prefilled_email;

        const form = $form({ email, password });

        this.content.ui_add([
            $el.FORM('w-block l-form m-center', [
                $el.H2('title', $i18n.tl.h_login),
                email,
                password,
                $el.DIV('s-small'),
                new UIButton({
                    title: $i18n.tl.b_login, classes: 'ui-tint-reverse', on_click: () => {
                        if(!form.validate()) return;
                        return manager.emailLogin(email.value, password.value).then(() => { this.dismiss(true) });
                    }
                }),
                !manager.options.no_forgot && new UIButton({
                    kind: 'k-text', classes: 'm-small', title: $i18n.tl.b_forgot, on_click: () => {
                        new ForgotDialog(manager, email.value).present({ replace: this })
                    }
                }),
                (manager.options.on_identity || manager.options.on_signup) && $el.DIV('s-medium'),
                manager.options.on_identity && new UIButton({
                    left_icon: UIIcon.BRAND_APPLE, classes: 'm-apple', title: $i18n.tl.b_ap_login,
                    on_click: () => manager.appleLogin().then(() => { this.dismiss(true) })
                }),
                manager.options.on_signup && new UIButton({
                    kind: 'k-text', classes: 'm-small', title: $i18n.tl.b_not_member, on_click: () => {
                        this.dismiss(true);
                        manager.options.on_signup!();
                    }
                }),
                $recaptcha.makeWarning('p-f-small b b-text')
            ])
        ]);
    }
}

class SignupDialog extends UIDialog {
    constructor(manager: ABLoginManager) {
        super({ close: manager.options.close, classes: ['m-440', manager.options.mobile_fs && 'm-mobile-fs'] });

        const first_name = new UITextInput({ placeholder: $i18n.tl.ph_first_name, autofill: UIAutofill.FIRST_NAME, required: true });
        const last_name = new UITextInput({ placeholder: $i18n.tl.ph_last_name, autofill: UIAutofill.LAST_NAME, required: true });
        const email = new UITextInput({ type: 'email', placeholder: $i18n.tl.ph_email, autofill: UIAutofill.EMAIL, required: true });
        const password = new UITextInput({ type: 'password', autofill: UIAutofill.NEW_PASSWORD, placeholder: $i18n.tl.ph_password, required: true });

        email.ui_add(new ABEmailCheck(email));

        if(manager.options.prefilled_email)
            email.value = manager.options.prefilled_email;

        let form = $form({ email: email, first_name: first_name, last_name: last_name, password: password })

        this.content.ui_add([
            $el.FORM('w-block l-form m-center', [
                $el.H2('title', $i18n.tl.m_signup),
                first_name,
                last_name,
                email,
                password,
                $el.DIV('s-small'),
                new UIButton({
                    title: $i18n.tl.b_confirm, classes: 'ui-tint-reverse', on_click: () => {
                        if(!form.validate()) return;
                        return manager.emailSignup(form.value).then(() => { this.dismiss(true) });
                    }
                })
            ])
        ]);
    }
}

class ForgotDialog extends UIDialog {
    constructor(manager: ABLoginManager, email: string) {
        super({ close: manager.options.close, classes: ['m-440', manager.options.mobile_fs && 'm-mobile-fs'] });

        const email_input = new UITextInput({ type: 'email', autofill: UIAutofill.EMAIL, placeholder: $i18n.tl.ph_email, required: true });
        email_input.value = email;

        const form = $form({ email: email_input });

        this.content.ui_add([
            $el.FORM('w-block l-form m-center', [
                $el.H2('title', $i18n.tl.h_forgot),
                email_input,
                new UIButton({
                    title: $i18n.tl.b_send, classes: 'ui-tint-reverse', on_click: () => {
                        if(!form.validate()) return;
                        $analytics.track('Password request');
                        const email = email_input.value;
                        meForgotPassword(manager.scope, email).then(() => {
                            new ForgotSentDialog(manager, email).present({ replace: this });
                        }, uiToast);
                    }
                }),
                new UIButton({
                    kind: 'k-text', classes: 'ui-tint-reverse', title: $i18n.tl.b_back, on_click: () => {
                        manager.showLoginDialog(this, true);
                    }
                })
            ])
        ]);
    }
}

class ForgotSentDialog extends UIDialog {
    constructor(manager: ABLoginManager, email: string) {
        super({ close: manager.options.close, classes: ['m-440', manager.options.mobile_fs && 'm-mobile-fs'] });

        this.content.ui_add([
            $el.DIV('w-block l-form m-center', [
                $el.DIV('r-top', $i18n.tl.t_forgot_sent + ' ' + email),
                new UIButton({
                    title: $i18n.tl.b_ok, classes: 'ui-tint-reverse', on_click: () => {
                        manager.showLoginDialog(this);
                    }
                }),
                $el.DIV('r-bottom', $i18n.tl.t_forgot_spam)
            ])
        ]);
    }
}

class ResetDialog extends UIDialog {
    constructor(manager: ABLoginManager) {
        super({ close: manager.options.close, classes: ['m-440', manager.options.mobile_fs && 'm-mobile-fs'] });

        const password = new UITextInput({ type: 'password', placeholder: $i18n.tl.ph_password, required: true });
        const form = $form({ password: password });

        this.content.ui_add([
            $el.FORM('w-block l-form m-center', [
                $el.H2('title', $i18n.tl.h_reset_pw),
                $el.P('input-hint', $i18n.tl.t_reset_pw),
                password,
                new UIButton({
                    title: $i18n.tl.b_reset_pw, classes: 'ui-tint-reverse', on_click: () => {
                        if(!form.validate()) return;

                        meResetPassword(manager.scope, password.value).then(() => {
                            $analytics.track('Password reset');
                            this.dismiss(true);
                            manager.options.on_success();
                        }, uiToast);
                    }
                })
            ])
        ]);
    }
}
